import {EmailBlock, parseBlocks} from "../types/emailBlocks";

/**
 * Converts a single HTML content string to plain text.
 * - Preserves links as "text (url)" format
 * - Preserves {{ }} placeholders
 * - Removes all HTML tags
 */
function htmlContentToPlainText(html: string): string {
    if (!html) return "";

    // Create a temporary DOM element to parse HTML
    const doc = new DOMParser().parseFromString(html, "text/html");

    // Process links: convert <a href="url">text</a> to "text (url)"
    const links = doc.querySelectorAll("a[href]");
    links.forEach((link) => {
        const href = link.getAttribute("href") || "";
        const text = link.textContent || "";

        // Skip if href is empty or is a placeholder
        if (!href || href.startsWith("{{")) {
            return;
        }

        // Skip if text equals href (avoid "url (url)")
        if (text.trim() === href.trim()) {
            link.textContent = href;
        } else {
            link.textContent = `${text} (${href})`;
        }
    });

    // Convert <br> to newlines
    const brs = doc.querySelectorAll("br");
    brs.forEach((br) => br.replaceWith("\n"));

    // Convert <p> tags to paragraphs (add newlines after each)
    const paragraphs = doc.querySelectorAll("p");
    paragraphs.forEach((p) => {
        p.insertAdjacentText("afterend", "\n\n");
    });

    // Get text content (strips all remaining HTML tags)
    let text = doc.body.textContent || "";

    // Clean up multiple newlines (max 2)
    text = text.replace(/\n{3,}/g, "\n\n").trim();

    return text;
}

/**
 * Converts email blocks (JSON string or array) to plain text.
 * Only processes the template content, not layout elements.
 *
 * @param blocksOrJson The blocks array or JSON string
 * @returns Plain text representation with blocks separated by blank lines
 */
export function blocksToPlainText(blocksOrJson: string | EmailBlock[]): string {
    const blocks = typeof blocksOrJson === "string"
        ? parseBlocks(blocksOrJson)
        : blocksOrJson;

    if (blocks.length === 0) {
        return "";
    }

    const textParts: string[] = [];

    for (const block of blocks) {
        const text = htmlContentToPlainText(block.content);
        if (text) {
            textParts.push(text);
        }
    }

    // Join blocks with double newline (paragraph separation)
    return textParts.join("\n\n");
}
